import { useEffect, FormEventHandler, ReactNode } from 'react';
import { GuestLayout } from '@/Layouts/GuestLayout';
import { InputError } from '@/Components/InputError';
import { InputLabel } from '@/Components/InputLabel';
import { PrimaryButton } from '@/Components/PrimaryButton';
import TextInput from '@/Components/TextInput';
import { Link, useForm } from '@inertiajs/react';
import { Head } from '@/Components/Head';
import { useQueryParam } from '@/hook/useQueryParam';
import { EMAIL_QUERY_ARG, NAME_QUERY_ARG } from '@/types/query.type';
import { PasswordInput } from '@/Components/PasswordInput';
import { PasswordStrengthBar } from '@/Components/PasswordStrengthBar';
import classNames from 'classnames';

export default function Signup() {
    //
    // TODO: Refactor using "/app/Enums/ApiErrorCode.php"
    //
    // The string should match the error message in the backend (SignupRequest.php).
    const EMAIL_ALREADY_EXISTS =
        'You already have an account. Please sign in here';

    const [email, _setEmail] = useQueryParam<string>({
        name: EMAIL_QUERY_ARG,
        defaultValue: '',
    });
    const [name, _setName] = useQueryParam<string>({
        name: NAME_QUERY_ARG,
        defaultValue: '',
    });

    const { data, setData, post, processing, errors, reset, setError } =
        useForm({
            name: name,
            email: email,
            password: '',
        });

    useEffect(() => {
        return () => {
            reset('password');
        };
    }, []);

    const submit: FormEventHandler = (event) => {
        event.preventDefault();

        post(route('signup'));
    };

    function replaceAnchorTags(
        errorMessage?: string,
    ): string | ReactNode | null {
        if (!errorMessage) return null;

        if (errorMessage === EMAIL_ALREADY_EXISTS) {
            return (
                <span>
                    {errorMessage.replace('sign in here', '')}{' '}
                    <Link href={route('login')} className="transition-all underline hover:text-brand-teal">
                        sign in here
                    </Link>
                </span>
            );
        }

        return errorMessage;
    }

    return (
        <GuestLayout showEmergencyBanner>
            <Head title="ReelFlow Signup" />

            <img src="/img/logo-motif.png" className="w-[100px] my-4" />

            <h4 className="text-brand-blue font-semibold font-area-normal">
                Get started with ReelFlow
            </h4>

            <div className="text-brand-blue text-body-text-15">
                14-day free trial - no credit card required.
            </div>

            <div className="text-brand-blue text-body-text-15">
                Interactive video on your websites and landing pages in no time
                at all! Want a quick demo instead? Book here
            </div>

            <div className="text-brand-blue text-body-text-15">
                Want a quick demo instead?{' '}
                <a
                    href="https://www.reelflow.com/demo"
                    target="_blank"
                    className="underline hover:text-brand-teal transition-colors duration-200">
                    Book here
                </a>
            </div>

            <form onSubmit={submit}>
                <div>
                    <InputLabel htmlFor="email" value="Work Email" />

                    <TextInput
                        id="email"
                        type="email"
                        name="email"
                        value={data.email}
                        className="mt-1 block w-full"
                        autoComplete="username"
                        onChange={(e) => setData('email', e.target.value)}
                        required
                    />

                    <InputError
                        message={replaceAnchorTags(errors.email)}
                        className="mt-2"
                    />
                </div>

                <div className="mt-2">
                    <InputLabel htmlFor="name" value="Name" />

                    <TextInput
                        id="name"
                        type="name"
                        name="name"
                        value={data.name}
                        className="mt-1 block w-full"
                        autoComplete="name"
                        onChange={(e) => setData('name', e.target.value)}
                        required
                    />

                    <InputError message={errors.name} className="mt-2" />
                </div>

                <div className="mt-2">
                    <InputLabel htmlFor="password" value="Password" />
                    <PasswordInput
                        id="password"
                        name="password"
                        value={data.password}
                        className={classNames(
                            'mt-1 block w-full pr-8',
                            errors.password
                                ? '!border-orange-500 focus:!border-orange-500 focus:!ring-orange-500/[0.35]'
                                : '',
                        )}
                        autoComplete="new-password"
                        isPasswordButtonVisible={true}
                        onChange={(e) => {
                            setData('password', e.target.value);
                            if (errors.password) {
                                setError('password', '');
                            }
                        }}
                    />
                    <PasswordStrengthBar
                        className="mt-1 w-full"
                        password={data.password}
                    />

                    <InputError message={errors.password} className="mt-2" />
                </div>

                <div className="mt-2">
                    <div className="text-sm text-brand-blue mt-4">
                        {`By clicking continue, you agree to ReelFlow's `}
                        <a
                            href="https://www.reelflow.com/legal/terms-of-service-starter"
                            target="_blank"
                            className="transition-all underline hover:text-brand-teal">
                            terms
                        </a>
                        {` and `}
                        <a
                            href="https://reelflow.com/legal/privacy"
                            target="_blank"
                            className="transition-all underline hover:text-brand-teal">
                            privacy agreements
                        </a>
                    </div>

                    <div>
                        <PrimaryButton
                            className="mt-3 w-full"
                            disabled={processing}>
                            Continue
                        </PrimaryButton>
                    </div>

                    <div className="mt-3">
                        <span className="text-sm text-brand-blue">
                            Already have an account?{' '}
                        </span>

                        <Link
                            href={route('login')}
                            className="transition-all underline hover:text-brand-teal text-sm text-brand-blue rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue">
                            Sign in
                        </Link>
                    </div>
                </div>
            </form>
        </GuestLayout>
    );
}
